/**
 * One message at a time, from the design system's toast.
 *
 * Same queue as the Studio's (`useStudioToastState` — React Aria's, so the message is
 * announced without stealing focus and hovering pauses the timer), and the same one-at-a-time
 * rule. No Undo is wired here: the admin's reversible actions (archive, unpublish) each go
 * through a confirm that states the consequence first, and a toast that offered to undo a
 * decision someone just confirmed would be noise.
 */

import { ToastRegion, useStudioToastState } from '@atlas-nook/design-system';
import { createContext, useCallback, useContext, useMemo } from 'react';
import type { JSX, ReactNode } from 'react';

const DISMISS_MS = 6000;

interface ToastValue {
  show: (message: ReactNode) => void;
}

const ToastContext = createContext<ToastValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const state = useStudioToastState();

  const show = useCallback(
    (message: ReactNode) => {
      for (const toast of [...state.visibleToasts]) state.close(toast.key);
      state.add({ message }, { timeout: DISMISS_MS });
    },
    [state],
  );

  const value = useMemo<ToastValue>(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastRegion state={state} label="Messages" />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastValue {
  // Outside a provider a message is a no-op rather than a crash: losing one must never take
  // the surface down with it.
  return useContext(ToastContext) ?? { show: () => undefined };
}
